/* ============================================
   FALGUN THEME — JAVASCRIPT
   ============================================ */
(() => {
  'use strict';

  // ── Helpers ──────────────────────────────────────────────────
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  function formatMoney(cents) {
    return '£' + (cents / 100).toFixed(2);
  }

  function escapeHtml(str) {
    return String(str ?? '').replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  // Resize a Shopify CDN image via the `width` query param. Handles absolute
  // and protocol-relative URLs, and URLs that already carry query params
  // (e.g. `?v=` cache-busters or an existing `?width=`).
  function sizedImageUrl(src, width) {
    if (!src) return src;
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (e) {
      return src;
    }
  }

  // ── Cart API ─────────────────────────────────────────────────
  async function fetchCart() {
    const r = await fetch('/cart.js');
    if (!r.ok) throw new Error('Could not load basket');
    return r.json();
  }

  async function cartAdd(variantId, quantity = 1) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.description || 'Could not add to cart');
    }
    return r.json();
  }

  async function cartChange(key, quantity) {
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.description || 'Could not update basket');
    }
    return r.json();
  }

  // Shopify doesn't bind a cart to the logged-in customer until checkout
  // re-prices it, so segment-scoped automatic discounts (the 5% "New
  // Customers" offer) are missing from the first cart/drawer view (#45).
  // For a logged-in customer with an undiscounted cart, force one
  // server-side re-price and return the refreshed cart. Attempted once per
  // cart token per tab session so ineligible customers don't retrigger it.
  async function ensureCartRepriced(cart) {
    if (!window.__falgunCustomerLoggedIn) return cart;
    if (!cart.item_count || cart.total_price !== cart.original_total_price) return cart;
    const attemptedKey = 'falgun:repriced:' + cart.token;
    try {
      if (sessionStorage.getItem(attemptedKey)) return cart;
      sessionStorage.setItem(attemptedKey, '1');
    } catch (e) { return cart; }
    try {
      await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      let fresh = await fetchCart();
      if (fresh.total_price === fresh.original_total_price) {
        // The update alone didn't attach buyer identity; touching checkout
        // does (confirmed on the live store). Opaque response is fine — only
        // the server-side side effect matters.
        await fetch('/checkout', { mode: 'no-cors' }).catch(() => {});
        fresh = await fetchCart();
      }
      return fresh;
    } catch (e) { return cart; }
  }

  // ── Cart Count Badge ─────────────────────────────────────────
  function setCartCount(count) {
    $$('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.dataset.count = count;
    });
  }

  // ── Cart Drawer Render ────────────────────────────────────────
  function renderCartDrawer(cart) {
    setCartCount(cart.item_count);

    const totalEl = $('#CartDrawerTotal');
    if (totalEl) totalEl.textContent = formatMoney(cart.original_total_price);

    // Discount + final-total rows only appear when a discount is active
    // (e.g. the logged-in-only "New Customers" 5% automatic discount).
    const savings = cart.original_total_price - cart.total_price;
    const discountRow = $('#CartDrawerDiscountRow');
    const finalRow = $('#CartDrawerFinalRow');
    if (discountRow && finalRow) {
      if (savings > 0) {
        const app = (cart.cart_level_discount_applications || [])[0];
        $('#CartDrawerDiscountLabel').textContent = app ? app.title : 'Discount';
        $('#CartDrawerDiscountAmount').textContent = '−' + formatMoney(savings);
        $('#CartDrawerFinalTotal').textContent = formatMoney(cart.total_price);
        discountRow.hidden = false;
        finalRow.hidden = false;
      } else {
        discountRow.hidden = true;
        finalRow.hidden = true;
      }
    }

    const bodyEl = $('#CartDrawerBody');
    if (!bodyEl) return;

    if (cart.items.length === 0) {
      bodyEl.innerHTML = `
        <div class="cart-drawer__empty">
          <div class="cart-drawer__empty-icon">🛒</div>
          <p style="font-size:16px;font-weight:600;color:var(--navy);margin-bottom:8px">Your basket is empty</p>
          <p style="font-size:14px;color:var(--text-lt);margin-bottom:24px">Add some products to get started</p>
          <a href="/collections/all" class="btn btn--royal btn--sm">Start Shopping</a>
        </div>`;
      return;
    }

    bodyEl.innerHTML = cart.items.map(item => `
      <div class="cart-drawer__item" data-key="${item.key}">
        <div class="cart-drawer__item-img">
          ${item.image
            ? `<img src="${sizedImageUrl(item.image, 120)}" alt="${escapeHtml(item.title)}" loading="lazy">`
            : '<span style="font-size:28px">📦</span>'}
        </div>
        <div>
          <div class="cart-drawer__item-name">${escapeHtml(item.product_title)}</div>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<div class="cart-drawer__item-variant">${escapeHtml(item.variant_title)}</div>`
            : ''}
          <div class="cart-drawer__item-qty">
            <button class="qty-btn-sm" data-action="dec" data-key="${item.key}" aria-label="Decrease">−</button>
            <span class="qty-display">${item.quantity}</span>
            <button class="qty-btn-sm" data-action="inc" data-key="${item.key}" aria-label="Increase">+</button>
          </div>
          <button class="cart-drawer__remove" data-action="remove" data-key="${item.key}">Remove</button>
        </div>
        <div class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</div>
      </div>
    `).join('');

    // Qty / remove handlers
    bodyEl.querySelectorAll('[data-action]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const item = cart.items.find(i => i.key === key);
        if (!item) return;
        let qty = item.quantity;
        if (btn.dataset.action === 'inc') qty += 1;
        else if (btn.dataset.action === 'dec') qty = Math.max(0, qty - 1);
        else qty = 0;

        btn.disabled = true;
        try {
          const updated = await cartChange(key, qty);
          renderCartDrawer(updated);
        } catch (err) {
          // 422 (e.g. qty past available stock) or network failure — re-sync
          // the drawer from the real cart state and tell the user why.
          try {
            renderCartDrawer(await fetchCart());
          } catch (e) {
            btn.disabled = false;
          }
          showCartDrawerMessage(err.message || 'Could not update basket');
        }
      });
    });
  }

  // Small dismiss-after-a-moment error message inside the cart drawer.
  // Lives outside #CartDrawerBody so re-renders don't wipe it.
  function showCartDrawerMessage(message) {
    const bodyEl = $('#CartDrawerBody');
    if (!bodyEl || !bodyEl.parentNode) { alert(message); return; }
    let msg = bodyEl.parentNode.querySelector('.cart-drawer__message');
    if (!msg) {
      msg = document.createElement('div');
      msg.className = 'cart-drawer__message';
      msg.setAttribute('role', 'alert');
      msg.style.cssText = 'margin:10px 16px 0;padding:10px 12px;border-radius:6px;background:#fdecea;color:#c0392b;font-size:13px;';
      bodyEl.parentNode.insertBefore(msg, bodyEl);
    }
    msg.textContent = message;
    clearTimeout(msg._hideTimer);
    msg._hideTimer = setTimeout(() => msg.remove(), 5000);
  }

  // ── Cart Drawer Open/Close ────────────────────────────────────
  function openCartDrawer() {
    const drawer = $('#CartDrawer');
    if (!drawer) return;
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    fetchCart().then(ensureCartRepriced).then(renderCartDrawer).catch(err => {
      showCartDrawerMessage(err.message || 'Could not load basket');
    });
  }

  function closeCartDrawer() {
    const drawer = $('#CartDrawer');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  // FAL-67: expose drawer open/close so other scripts (e.g. product-form.js) can
  // open the side cart drawer instead of redirecting to /cart after add-to-cart.
  window.FalgunCart = {
    open: openCartDrawer,
    close: closeCartDrawer,
    refresh: () => fetchCart().then(ensureCartRepriced).then(renderCartDrawer).catch(() => {}),
  };

  // ── Mobile Nav ────────────────────────────────────────────────
  function openMobileNav() {
    const nav = $('#MobileNav');
    if (!nav) return;
    nav.classList.add('is-open');
    nav.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeMobileNav() {
    const nav = $('#MobileNav');
    if (!nav) return;
    nav.classList.remove('is-open');
    nav.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  // ── Add to Cart (product cards quick-add) ────────────────────
  async function quickAdd(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId || btn.classList.contains('is-loading')) return;

    btn.classList.add('is-loading');
    btn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>`;

    try {
      await cartAdd(variantId, 1);
      const cart = await fetchCart();
      setCartCount(cart.item_count);
      // FAL-67: open the side cart drawer instead of navigating away.
      openCartDrawer();
    } catch (err) {
      console.error(err);
      // Show error briefly
      btn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>`;
      setTimeout(() => resetAddBtn(btn), 2000);
      return;
    }

    setTimeout(() => resetAddBtn(btn), 1500);
  }

  function resetAddBtn(btn) {
    btn.classList.remove('is-loading');
    btn.innerHTML = `<svg viewBox="0 0 24 24" width="16" height="16"><path d="M12 5v14M5 12h14" stroke="white" stroke-width="2.2" stroke-linecap="round"/></svg>`;
  }

  // ── Product Page ──────────────────────────────────────────────
  function initProductPage() {
    // Gallery thumbnails
    $$('.product-gallery__thumb').forEach(thumb => {
      thumb.addEventListener('click', () => {
        $$('.product-gallery__thumb').forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
        const mainImg = $('#ProductMainImage');
        const thumbImg = thumb.querySelector('img');
        if (mainImg && thumbImg) {
          mainImg.src = sizedImageUrl(thumbImg.src, 800);
        }
      });
    });

    // Gallery thumbnails — prev/next scroll buttons (FAL-59)
    // Only active when there are more thumbs than fit (>5). The CSS hides the
    // buttons unless data-thumbs-overflow is set on the wrap.
    $$('[data-thumbs-wrap]').forEach(wrap => {
      const strip = wrap.querySelector('[data-thumbs-strip]');
      const prev  = wrap.querySelector('[data-thumbs-nav="prev"]');
      const next  = wrap.querySelector('[data-thumbs-nav="next"]');
      if (!strip || !prev || !next) return;

      function checkOverflow() {
        if (strip.scrollWidth > strip.clientWidth + 1) {
          wrap.setAttribute('data-thumbs-overflow', '');
        } else {
          wrap.removeAttribute('data-thumbs-overflow');
        }
      }

      function updateDisabled() {
        const max = strip.scrollWidth - strip.clientWidth;
        prev.toggleAttribute('disabled', strip.scrollLeft <= 1);
        next.toggleAttribute('disabled', strip.scrollLeft >= max - 1);
      }

      function pageScroll(direction) {
        // Scroll one page (~5 thumbs) at a time. clientWidth ~= one page width.
        strip.scrollBy({ left: direction * strip.clientWidth, behavior: 'smooth' });
      }

      prev.addEventListener('click', () => pageScroll(-1));
      next.addEventListener('click', () => pageScroll(1));
      strip.addEventListener('scroll', updateDisabled, { passive: true });
      window.addEventListener('resize', () => { checkOverflow(); updateDisabled(); });

      // Initial pass — defer slightly so layout is settled.
      checkOverflow();
      updateDisabled();
    });

    // Tab navigation
    $$('.product-tab-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        $$('.product-tab-btn').forEach(b => b.classList.remove('is-active'));
        $$('.product-tab-panel').forEach(p => p.classList.remove('is-active'));
        btn.classList.add('is-active');
        const panel = document.getElementById(btn.dataset.tab);
        if (panel) panel.classList.add('is-active');
      });
    });

    // Quantity selectors
    $$('.qty-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.closest('.qty-selector')?.querySelector('.qty-input');
        if (!input) return;
        const v = parseInt(input.value) || 1;
        input.value = btn.dataset.action === 'decrease' ? Math.max(1, v - 1) : v + 1;
      });
    });

    // Variant selection
    $$('.variant-option').forEach(opt => {
      opt.addEventListener('click', () => {
        opt.closest('.variant-options')?.querySelectorAll('.variant-option').forEach(o => o.classList.remove('is-active'));
        opt.classList.add('is-active');
        updateVariant();
      });
    });

    // Add to cart form
    const form = $('#ProductAtcForm');
    if (form) {
      form.addEventListener('submit', async e => {
        e.preventDefault();
        const btn = form.querySelector('[data-atc-btn]');
        const variantId = form.querySelector('[name="id"]')?.value;
        const qty = parseInt(form.querySelector('[name="quantity"]')?.value || '1');
        if (!variantId) return;

        if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
        try {
          await cartAdd(variantId, qty);
          const cart = await fetchCart();
          setCartCount(cart.item_count);
          // FAL-67: stay on the page; open the right-side cart drawer instead
          // of redirecting to /cart.
          openCartDrawer();
        } catch (err) {
          alert(err.message || 'Could not add to basket. Please try again.');
        } finally {
          if (btn) { btn.disabled = false; btn.textContent = btn.dataset.labelAvailable || 'Add to Basket'; }
        }
      });
    }

    // Capture the "available" ATC label once from the DOM, then make sure the
    // initial UI is consistent if the default-selected variant is sold out
    // (every product here has a permanently sold-out "Single" variant, so this
    // path is hit constantly).
    const atcBtn = $('[data-atc-btn]');
    if (atcBtn && !atcBtn.dataset.labelAvailable) {
      atcBtn.dataset.labelAvailable = atcBtn.classList.contains('btn--sold-out')
        ? 'Add to Basket'
        : atcBtn.textContent.trim();
    }
    const initialId = parseInt($('#ProductAtcForm [name="id"]')?.value, 10);
    const initialVariant = (window.__variants || []).find(v => v.id === initialId);
    if (initialVariant && !initialVariant.available) setProductAvailability(false);
  }

  // Sync every availability-dependent piece of the product page UI:
  // ATC button (label + disabled + sold-out styling), the stock-status row
  // and the "email me when back in stock" link.
  function setProductAvailability(available) {
    const atcBtn = document.querySelector('[data-atc-btn]');
    if (atcBtn) {
      if (!atcBtn.dataset.labelAvailable) {
        atcBtn.dataset.labelAvailable = atcBtn.classList.contains('btn--sold-out')
          ? 'Add to Basket'
          : atcBtn.textContent.trim();
      }
      atcBtn.disabled = !available;
      atcBtn.classList.toggle('btn--sold-out', !available);
      atcBtn.textContent = available ? atcBtn.dataset.labelAvailable : 'Sold Out';
      if (available) atcBtn.removeAttribute('aria-disabled');
      else atcBtn.setAttribute('aria-disabled', 'true');
    }
    const stockEl = document.querySelector('[data-stock-status]');
    if (stockEl) {
      stockEl.innerHTML = available
        ? '<span class="stock-dot stock-dot--in"></span><span style="color:var(--color-success)">In Stock</span>'
        : '<span class="stock-dot stock-dot--out"></span><span style="color:var(--color-error)">Out of Stock</span>';
    }
    const notifyEl = document.querySelector('[data-restock-notify]');
    if (notifyEl) notifyEl.toggleAttribute('hidden', available);
  }

  function updateVariant() {
    const selected = $$('.variant-option.is-active').map(o => o.dataset.value);
    const variants = window.__variants || [];
    const match = variants.find(v =>
      [v.option1, v.option2, v.option3].filter(Boolean).every((o, i) => o === selected[i])
    );
    if (!match) return;
    const input = document.querySelector('[name="id"]');
    if (input) input.value = match.id;
    const priceEl = document.querySelector('.product-info__price');
    if (priceEl) priceEl.textContent = formatMoney(match.price);
    setProductAvailability(!!match.available);
  }

  // ── Collection page ───────────────────────────────────────────
  // Sorting (applySort) and the filter-group accordion are handled by the
  // inline script in sections/main-collection.liquid — only the mobile
  // filter toggle lives here (it also keeps the button label in sync).
  function initCollectionPage() {
    // Mobile filter toggle
    const filterBtn = $('#FilterToggle');
    const sidebar = $('.filters-sidebar');
    if (filterBtn && sidebar) {
      filterBtn.addEventListener('click', () => {
        sidebar.classList.toggle('is-mobile-open');
        filterBtn.textContent = sidebar.classList.contains('is-mobile-open') ? 'Hide Filters' : 'Show Filters';
      });
    }
  }

  // ── Quick View Modal ──────────────────────────────────────────
  const QuickView = (() => {
    let modal, panel, loadingEl, errorEl, bodyEl;
    let mainImg, thumbsEl, badgesEl, vendorEl, titleEl, priceEl, ratingEl, ratingCountEl;
    let variantsEl, viewDetailsEl, addBtn, fallbackLink, prevBtn, nextBtn;
    const cache = new Map();
    const state = {
      product: null,
      images: [],
      imageIndex: 0,
      selectedOptions: [],
      currentVariant: null,
      handle: null,
    };

    function ensureRefs() {
      if (modal) return true;
      modal = $('#QuickViewModal');
      if (!modal) return false;
      panel        = modal.querySelector('.quick-view__panel');
      loadingEl    = modal.querySelector('[data-quickview-loading]');
      errorEl      = modal.querySelector('[data-quickview-error]');
      bodyEl       = modal.querySelector('[data-quickview-body]');
      mainImg      = modal.querySelector('[data-quickview-image]');
      thumbsEl     = modal.querySelector('[data-quickview-thumbs]');
      badgesEl     = modal.querySelector('[data-quickview-badges]');
      vendorEl     = modal.querySelector('[data-quickview-vendor]');
      titleEl      = modal.querySelector('[data-quickview-title]');
      priceEl      = modal.querySelector('[data-quickview-price]');
      ratingEl     = modal.querySelector('[data-quickview-rating]');
      ratingCountEl= modal.querySelector('[data-quickview-rating-count]');
      variantsEl   = modal.querySelector('[data-quickview-variants]');
      viewDetailsEl= modal.querySelector('[data-quickview-view-details]');
      addBtn       = modal.querySelector('[data-quickview-add]');
      fallbackLink = modal.querySelector('[data-quickview-fallback-link]');
      prevBtn      = modal.querySelector('[data-quickview-prev]');
      nextBtn      = modal.querySelector('[data-quickview-next]');
      return true;
    }

    function setHidden(el, hidden) {
      if (!el) return;
      if (hidden) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    }

    function open() {
      if (!ensureRefs()) return;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function close() {
      if (!modal) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    function isOpen() {
      return modal && modal.classList.contains('is-open');
    }

    function showLoading() {
      setHidden(loadingEl, false);
      setHidden(errorEl, true);
      setHidden(bodyEl, true);
    }

    function showError(productUrl) {
      setHidden(loadingEl, true);
      setHidden(bodyEl, true);
      setHidden(errorEl, false);
      if (fallbackLink && productUrl) fallbackLink.setAttribute('href', productUrl);
    }

    function showBody() {
      setHidden(loadingEl, true);
      setHidden(errorEl, true);
      setHidden(bodyEl, false);
    }

    async function fetchProduct(handle) {
      if (cache.has(handle)) return cache.get(handle);
      const r = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
      if (!r.ok) throw new Error(`Could not load product ${handle}`);
      const data = await r.json();
      cache.set(handle, data);
      return data;
    }

    function setImage(index) {
      if (!state.images.length) return;
      state.imageIndex = ((index % state.images.length) + state.images.length) % state.images.length;
      if (mainImg) {
        mainImg.src = state.images[state.imageIndex];
        mainImg.alt = state.product?.title || '';
      }
      thumbsEl?.querySelectorAll('.quick-view__thumb').forEach((t, i) => {
        t.classList.toggle('is-active', i === state.imageIndex);
      });
    }

    function renderThumbs() {
      if (!thumbsEl) return;
      thumbsEl.innerHTML = state.images.map((src, i) => `
        <button type="button" class="quick-view__thumb${i === state.imageIndex ? ' is-active' : ''}" data-index="${i}" aria-label="View image ${i + 1}">
          <img src="${src}" alt="" loading="lazy">
        </button>
      `).join('');
      thumbsEl.querySelectorAll('.quick-view__thumb').forEach(btn => {
        btn.addEventListener('click', () => setImage(parseInt(btn.dataset.index, 10) || 0));
      });
    }

    function renderBadges() {
      if (!badgesEl) return;
      const tags = (state.product?.tags || []).map(t => String(t).toLowerCase());
      const badges = [];
      // NEW — tag-based OR auto-detected from product.created_at within the last 14 days.
      // To change the window, edit NEW_WINDOW_DAYS below (mirrors product-flags.liquid default).
      const NEW_WINDOW_DAYS = 14;
      let isNew = tags.includes('new');
      if (!isNew && state.product?.created_at) {
        const createdMs = Date.parse(state.product.created_at);
        if (!isNaN(createdMs)) {
          const ageDays = (Date.now() - createdMs) / 86400000;
          if (ageDays >= 0 && ageDays <= NEW_WINDOW_DAYS) isNew = true;
        }
      }
      if (isNew) badges.push({ text: 'NEW!', cls: 'quick-view__badge--new' });
      // BESTSELLER — tag-based OR auto-detected from custom.units_sold_recent metafield.
      const BESTSELLER_THRESHOLD = 100;
      let isBestseller = tags.some(t => t === 'bestseller' || t === 'best-seller');
      const unitsSold = Number(state.product?.metafields?.custom?.units_sold_recent || 0);
      if (!isBestseller && unitsSold >= BESTSELLER_THRESHOLD) isBestseller = true;
      if (isBestseller) badges.push({ text: 'BESTSELLER', cls: 'quick-view__badge--bestseller' });
      if (state.currentVariant && state.currentVariant.compare_at_price && state.currentVariant.compare_at_price > state.currentVariant.price) {
        badges.push({ text: 'SALE', cls: 'quick-view__badge--sale' });
      }
      badgesEl.innerHTML = badges.map(b => `<span class="quick-view__badge ${b.cls}">${escapeHtml(b.text)}</span>`).join('');
    }

    function renderPrice() {
      if (!priceEl) return;
      const v = state.currentVariant;
      if (!v) { priceEl.textContent = ''; return; }
      const compare = v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : null;
      priceEl.innerHTML = `
        <span>${formatMoney(v.price)}</span>
        ${compare ? `<span class="was">${formatMoney(compare)}</span>` : ''}
      `;
    }

    function renderRating() {
      if (!ratingEl || !ratingCountEl) return;
      // /products/{handle}.js does not include metafields, so this mirrors the
      // static display used on product cards. Replace with real review data
      // when reviews are integrated.
      ratingCountEl.textContent = '';
      ratingEl.style.display = '';
    }

    function findMatchingVariant() {
      if (!state.product) return null;
      return state.product.variants.find(v =>
        v.options.every((opt, i) => opt === state.selectedOptions[i])
      ) || null;
    }

    function updateVariant() {
      state.currentVariant = findMatchingVariant();
      renderPrice();
      renderBadges();
      updateAddBtn();
      markUnavailableOptions();
      // Swap to variant featured image if it exists
      if (state.currentVariant && state.currentVariant.featured_image) {
        const variantImgSrc = state.currentVariant.featured_image.src;
        const sized = sizedImage(variantImgSrc, 800);
        const idx = state.images.findIndex(src =>
          src === sized ||
          src === variantImgSrc ||
          src.replace(/^https?:/, '') === sized.replace(/^https?:/, '')
        );
        if (idx >= 0) setImage(idx);
      }
    }

    function updateAddBtn() {
      if (!addBtn) return;
      const v = state.currentVariant;
      const labelEl = addBtn.querySelector('[data-quickview-add-label]') || addBtn;
      if (!v) {
        addBtn.disabled = true;
        labelEl.textContent = 'Unavailable';
        return;
      }
      if (!v.available) {
        addBtn.disabled = true;
        labelEl.textContent = 'Sold Out';
        return;
      }
      addBtn.disabled = false;
      labelEl.textContent = 'Add To Bag';
    }

    function isColorOption(name) {
      const n = String(name || '').toLowerCase();
      return n === 'color' || n === 'colour';
    }

    function renderVariants() {
      if (!variantsEl || !state.product) return;
      const product = state.product;

      // Shopify's /products/{handle}.js may return options either as plain strings
      // (legacy) or as objects ({ name, position, values }) — normalize to strings.
      const optionNames = (product.options || []).map(opt => {
        if (opt && typeof opt === 'object') return String(opt.name || '');
        return String(opt || '');
      });

      // Single-variant products with default-only option — hide picker
      const onlyDefault = optionNames.length === 1 &&
        optionNames[0].toLowerCase() === 'title' &&
        product.variants.length === 1 &&
        product.variants[0].title === 'Default Title';
      if (onlyDefault) {
        variantsEl.innerHTML = '';
        return;
      }

      const html = optionNames.map((optName, optIndex) => {
        const values = [];
        const seen = new Set();
        product.variants.forEach(v => {
          const val = v.options[optIndex];
          if (!seen.has(val)) { seen.add(val); values.push(val); }
        });

        const isColor = isColorOption(optName);
        const selected = state.selectedOptions[optIndex];

        const buttons = values.map(val => {
          const isActive = selected === val ? ' is-active' : '';
          if (isColor) {
            return `<button type="button" class="quick-view__swatch${isActive}" data-option-index="${optIndex}" data-option-value="${escapeHtml(val)}" style="background:${cssColor(val)};" title="${escapeHtml(val)}" aria-label="${escapeHtml(optName)}: ${escapeHtml(val)}"></button>`;
          }
          return `<button type="button" class="quick-view__size${isActive}" data-option-index="${optIndex}" data-option-value="${escapeHtml(val)}">${escapeHtml(val)}</button>`;
        }).join('');

        const valueDisplay = isColor
          ? `<span class="quick-view__option-label-value" data-qv-value-${optIndex}>${escapeHtml(selected || '')}</span>`
          : '';

        return `
          <div class="quick-view__option" data-option-group="${optIndex}">
            <div class="quick-view__option-label">
              <span>${escapeHtml(optName)}:</span>
              ${valueDisplay}
            </div>
            <div class="quick-view__option-values">${buttons}</div>
          </div>
        `;
      }).join('');

      variantsEl.innerHTML = html;

      variantsEl.querySelectorAll('[data-option-index]').forEach(btn => {
        btn.addEventListener('click', () => {
          const idx = parseInt(btn.dataset.optionIndex, 10);
          if (Number.isNaN(idx)) return;
          state.selectedOptions[idx] = btn.dataset.optionValue;
          // Toggle active state within this option group
          const group = btn.closest('[data-option-group]');
          group?.querySelectorAll('[data-option-index]').forEach(b => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          // Update color label if shown
          const valueLabel = group?.querySelector(`[data-qv-value-${idx}]`);
          if (valueLabel) valueLabel.textContent = btn.dataset.optionValue;
          updateVariant();
        });
      });

      // Mark unavailable options based on currently selected siblings
      markUnavailableOptions();
    }

    function markUnavailableOptions() {
      if (!variantsEl || !state.product) return;
      const product = state.product;
      variantsEl.querySelectorAll('[data-option-index]').forEach(btn => {
        const idx = parseInt(btn.dataset.optionIndex, 10);
        const val = btn.dataset.optionValue;
        // A value is "available" if there exists a variant matching all OTHER selected options
        // and this option = val, that is in stock
        const matches = product.variants.find(v => {
          if (v.options[idx] !== val) return false;
          return v.options.every((optVal, i) => i === idx || optVal === state.selectedOptions[i]);
        });
        const available = matches && matches.available;
        btn.classList.toggle('is-unavailable', !available);
      });
    }

    // Best-effort CSS color from option string. Falls back to a neutral.
    function cssColor(name) {
      const n = String(name || '').trim().toLowerCase();
      const map = {
        white: '#ffffff', black: '#111111', grey: '#888888', gray: '#888888',
        navy: '#0a1628', blue: '#1e4ea8', red: '#c0392b', green: '#1e8449',
        brown: '#6b4423', beige: '#d2b48c', cream: '#f5f5dc', tan: '#d2a679',
        pink: '#f48fb1', purple: '#7e57c2', yellow: '#f1c40f', orange: '#e67e22',
        khaki: '#9a8b59', olive: '#556b2f', burgundy: '#7a1f2b', charcoal: '#36454f',
      };
      // Use direct key match, else CSS named color, else neutral
      if (map[n]) return map[n];
      // CSS will accept any valid named color; bracket fallback
      return /^[a-z\s]+$/.test(n) ? n.replace(/\s+/g, '') : '#cccccc';
    }

    async function showFor(handle, productUrl) {
      if (!ensureRefs()) return;
      open();
      showLoading();
      state.handle = handle;
      try {
        const product = await fetchProduct(handle);
        state.product = product;
        // Default variant — first available, else first
        const initial = product.variants.find(v => v.available) || product.variants[0];
        state.currentVariant = initial || null;
        state.selectedOptions = initial ? [...initial.options] : [];

        // Build image list — featured first, then unique others
        const imgs = [];
        if (product.featured_image) imgs.push(product.featured_image);
        (product.images || []).forEach(src => { if (!imgs.includes(src)) imgs.push(src); });
        state.images = imgs.length ? imgs.map(src => sizedImage(src, 800)) : [];
        state.imageIndex = 0;

        // Populate
        if (titleEl)  titleEl.textContent = product.title;
        if (vendorEl) vendorEl.textContent = product.vendor || '';
        if (viewDetailsEl) viewDetailsEl.setAttribute('href', productUrl || product.url || `/products/${handle}`);

        renderBadges();
        renderPrice();
        renderRating();
        renderThumbs();
        setImage(0);
        renderVariants();
        updateAddBtn();

        showBody();
      } catch (err) {
        console.error('[QuickView]', err);
        showError(productUrl);
      }
    }

    function sizedImage(src, width) {
      if (!src) return src;
      // Shopify images: insert _{width}x before the extension
      return src.replace(/(\.[a-zA-Z]{3,4}(\?.*)?)$/, m => `_${width}x${m}`);
    }

    async function addToBag() {
      if (!state.currentVariant || !state.currentVariant.available || !addBtn) return;
      const labelEl = addBtn.querySelector('[data-quickview-add-label]') || addBtn;
      const originalLabel = labelEl.textContent;
      addBtn.disabled = true;
      labelEl.textContent = 'Adding…';
      try {
        await cartAdd(state.currentVariant.id, 1);
        const cart = await fetchCart();
        setCartCount(cart.item_count);
        labelEl.textContent = 'Added ✓';
        setTimeout(() => {
          if (!isOpen()) return;
          updateAddBtn();
          // Open cart drawer for confirmation
          close();
          openCartDrawer();
        }, 700);
      } catch (err) {
        console.error('[QuickView] add to cart failed', err);
        labelEl.textContent = 'Try Again';
        setTimeout(() => { labelEl.textContent = originalLabel; addBtn.disabled = false; }, 1500);
      }
    }

    function init() {
      if (!ensureRefs()) return;

      // Trigger delegation
      document.addEventListener('click', e => {
        const trigger = e.target.closest('[data-quickview-trigger]');
        if (!trigger) return;
        e.preventDefault();
        e.stopPropagation();
        const handle = trigger.dataset.productHandle;
        const url = trigger.dataset.productUrl || '';
        if (handle) showFor(handle, url);
      });

      // Close handlers
      modal.querySelectorAll('[data-quickview-close]').forEach(el => {
        el.addEventListener('click', close);
      });

      // Image nav
      prevBtn?.addEventListener('click', () => setImage(state.imageIndex - 1));
      nextBtn?.addEventListener('click', () => setImage(state.imageIndex + 1));

      // Add to bag
      addBtn?.addEventListener('click', addToBag);

      // Keyboard nav
      document.addEventListener('keydown', e => {
        if (!isOpen()) return;
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowLeft')  setImage(state.imageIndex - 1);
        else if (e.key === 'ArrowRight') setImage(state.imageIndex + 1);
      });
    }

    return { init, close, open, showFor };
  })();

  // ── Search bar typewriter placeholder ────────────────────────
  // FAL-72: animate the search input's placeholder through a rotating list of
  // prompts ("What are you looking for?" → "Search cake boxes…" → …) with a
  // typewriter effect. Pauses while the user has the input focused or has
  // typed anything, and respects prefers-reduced-motion.
  function initSearchTypewriter() {
    const inputs = $$('[data-typewriter]');
    if (!inputs.length) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    inputs.forEach(input => {
      let prompts;
      try {
        prompts = JSON.parse(input.getAttribute('data-typewriter-prompts') || '[]');
      } catch (e) {
        prompts = [];
      }
      if (!prompts.length) prompts = [input.placeholder || ''];
      // If the user prefers reduced motion just rotate prompts statically.
      if (prefersReduced) {
        let i = 0;
        input.placeholder = prompts[0];
        setInterval(() => {
          if (document.activeElement === input || input.value) return;
          i = (i + 1) % prompts.length;
          input.placeholder = prompts[i];
        }, 4000);
        return;
      }

      const TYPE_MS = 65;
      const ERASE_MS = 35;
      const HOLD_MS = 1800;
      let promptIdx = 0;
      let charIdx = 0;
      let mode = 'typing'; // typing | holding | erasing
      let timer = null;

      function paused() {
        return document.activeElement === input || !!input.value;
      }

      function tick() {
        if (paused()) {
          timer = setTimeout(tick, 400);
          return;
        }
        const target = prompts[promptIdx];
        if (mode === 'typing') {
          charIdx += 1;
          input.placeholder = target.slice(0, charIdx);
          if (charIdx >= target.length) {
            mode = 'holding';
            timer = setTimeout(tick, HOLD_MS);
            return;
          }
          timer = setTimeout(tick, TYPE_MS);
        } else if (mode === 'holding') {
          mode = 'erasing';
          timer = setTimeout(tick, ERASE_MS);
        } else if (mode === 'erasing') {
          charIdx -= 1;
          input.placeholder = target.slice(0, Math.max(0, charIdx));
          if (charIdx <= 0) {
            promptIdx = (promptIdx + 1) % prompts.length;
            mode = 'typing';
            timer = setTimeout(tick, 400);
            return;
          }
          timer = setTimeout(tick, ERASE_MS);
        }
      }

      // Start with empty placeholder so the typing in is visible.
      input.placeholder = '';
      timer = setTimeout(tick, 400);
    });
  }

  // ── Bootstrap ─────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', () => {

    // Initial cart count + first-landing discount re-price (#45). The cart
    // page renders totals server-side, so if the re-price changed them,
    // reload once — ensureCartRepriced's session guard prevents a loop.
    fetchCart().then(async cart => {
      setCartCount(cart.item_count);
      const fresh = await ensureCartRepriced(cart);
      if ($('#CartPageForm') && fresh.total_price !== cart.total_price) {
        window.location.reload();
      }
    }).catch(() => {});

    // FAL-72: kick off typewriter placeholder for header search.
    initSearchTypewriter();

    // Cart drawer triggers
    $$('[data-open-cart]').forEach(btn => btn.addEventListener('click', openCartDrawer));
    $('#CartDrawer')?.querySelector('.cart-drawer__close')?.addEventListener('click', closeCartDrawer);
    $('#CartDrawer')?.querySelector('.cart-drawer__overlay')?.addEventListener('click', closeCartDrawer);

    // Mobile nav
    $('#MobileMenuBtn')?.addEventListener('click', openMobileNav);
    $('.mobile-nav__close')?.addEventListener('click', closeMobileNav);
    $('.mobile-nav__overlay')?.addEventListener('click', closeMobileNav);

    // ESC key
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') { closeCartDrawer(); closeMobileNav(); }
    });

    // Legacy quick-add buttons (kept for any pages still using them)
    document.addEventListener('click', e => {
      const btn = e.target.closest('.add-to-cart-btn');
      if (btn) { e.preventDefault(); quickAdd(btn); }
    });

    // Quickview modal — delegated trigger handler + key/close bindings
    QuickView.init();

    // Page-specific
    if ($('.product-page'))    initProductPage();
    if ($('.collection-page')) initCollectionPage();
    // Cart page qty wiring lives in sections/main-cart.liquid.
  });

})();
